<template>
    <div>
        <!-- Section header. The icon is its own column so the title and sub-copy share a left edge. -->
        <div class="mb-4 flex items-start gap-2">
            <i v-if="otherTitle" class="bi bi-people-fill text-sm leading-6 text-ink shrink-0" aria-hidden="true"></i>
            <div class="min-w-0 flex-1">
                <!-- Title with the due date to the side. -->
                <div class="flex items-center justify-between gap-2">
                    <h2 class="scaffold-body truncate">{{ otherTitle }}</h2>
                    <span v-if="dateLabel" class="scaffold-body-small text-ink-muted whitespace-nowrap shrink-0">{{ dateLabel }}</span>
                </div>
                <!-- Sub-copy (left) and Edit team (right) share the line below; both are optional. -->
                <div v-if="otherSubtitle || shouldShowEditTeamButton" class="flex items-start gap-3 mt-1">
                    <p v-if="otherSubtitle" class="scaffold-body-small text-ink-muted min-w-0 whitespace-pre-line">{{ otherSubtitle }}</p>
                    <BaseButton v-if="shouldShowEditTeamButton" theme="tertiary" size="sm" class="shrink-0 ml-auto" @click="$emit('edit-list')">
                        {{ t("moments.people.editTeam") }}
                    </BaseButton>
                </div>
            </div>
        </div>
        <div class="flex flex-col gap-3">
            <!-- Empty state: user has opted into reviewing team members but hasn't added any yet -->
            <BaseCard v-if="!Object.keys(reviewsByRelationship).length" padding="lg" class="flex flex-col items-center justify-center gap-3 text-center">
                <i class="bi bi-people text-3xl text-ink-muted" aria-hidden="true"></i>
                <div class="body-small text-ink-muted">{{ t("moments.people.emptyState") }}</div>
                <BaseButton v-if="!isMomentReadOnly" theme="secondary" class="flex items-center gap-2" @click="$emit('edit-list')">
                    <i class="bi bi-plus-lg" aria-hidden="true"></i>
                    {{ t("moments.people.addTeamMembers") }}
                </BaseButton>
            </BaseCard>
            <!-- Grouped reviews by relationship -->
            <div v-for="(relationshipReviews, relationshipId) in reviewsByRelationship" :key="relationshipId" class="flex flex-col gap-3">
                <!-- My People Card -->
                <BaseCard padding="none" class="flex flex-col overflow-hidden">
                    <!-- Sits outside the header button: it holds its own buttons, which cannot nest inside one. -->
                    <div
                        v-if="relationshipReviews[0].relationship?.needs_confirmation && !isMomentReadOnly"
                        data-testid="hris-team-member-confirmation"
                        class="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-3 rounded-t-xl bg-accent-soft border-b border-border">
                        <p class="body-small text-ink grow">
                            {{ t("components.moments.reviewTeam.addedConfirmationPrompt", { name: relationshipReviews[0].relationship?.reviewee_name }) }}
                        </p>
                        <div class="flex gap-2 shrink-0">
                            <BaseButton theme="neutral" size="sm" :disabled="isResolvingTeamMember" @click="$emit('decline-relationship', String(relationshipId))">
                                {{ t("components.moments.reviewTeam.removeAddedMember") }}
                            </BaseButton>
                            <BaseButton theme="primary" size="sm" :disabled="isResolvingTeamMember" @click="$emit('confirm-relationship', String(relationshipId))">
                                {{ t("components.moments.reviewTeam.keepAddedMember") }}
                            </BaseButton>
                        </div>
                    </div>
                    <button
                        class="flex justify-between items-start px-5 py-5 cursor-pointer hover:bg-surface-hover rounded-t-xl text-start"
                        :class="{ 'rounded-b-xl': !expandedCards[relationshipId] }"
                        :aria-expanded="!!expandedCards[relationshipId]"
                        @click="toggleCard(relationshipId)">
                        <div class="flex flex-col gap-1">
                            <div class="body-bold" :class="isMomentReadOnly ? 'text-ink-muted' : 'text-ink'">
                                {{ relationshipReviews[0].relationship?.reviewee_name }}
                            </div>
                            <div class="body-small min-h-5 text-ink-muted">
                                {{ relationshipReviews[0].relationship?.reviewee_job_title }}
                            </div>
                            <div v-if="relationshipReviews[0].relationship?.seeded_from_hris" class="details flex items-center gap-2 text-ink-muted">
                                <i class="bi bi-diagram-3" aria-hidden="true"></i>
                                {{ t("components.moments.reviewTeam.seededFromReportingLine") }}
                            </div>
                        </div>
                        <div class="flex items-center gap-3">
                            <StatusChip
                                v-if="isSectionLocked || !(isMomentReadOnly && getReviewGroupStatusState(relationshipReviews) === 'neutral')"
                                size="xs"
                                :state="isSectionLocked ? 'neutral' : getReviewGroupStatusState(relationshipReviews)">
                                {{ isSectionLocked ? t("moments.people.comingSoon") : getReviewGroupStatus(relationshipReviews) }}
                            </StatusChip>
                            <i :class="expandedCards[relationshipId] ? 'bi-chevron-up' : 'bi-chevron-down'" class="bi text-ink-muted p-1" aria-hidden="true"></i>
                        </div>
                    </button>
                    <div :hidden="!expandedCards[relationshipId]" class="flex flex-col gap-1 overflow-hidden">
                        <HorizontalDivider class="my-0!" />
                        <PerformanceManagementCTA
                            v-for="review in relationshipReviews"
                            :key="review.id"
                            :review="review"
                            class="px-5"
                            :is-loading="!!currentReviewId && currentReviewId === review.id"
                            :is-disabled="!!currentReviewId && currentReviewId !== review.id"
                            :is-moment-read-only="isMomentReadOnly"
                            :is-coming-soon="isSectionLocked"
                            @cta-convo-clicked="(reviewId) => $emit('cta-convo-clicked', reviewId)"
                            @completion-toggled="(reviewId, complete) => $emit('completion-toggled', reviewId, complete)" />
                    </div>
                </BaseCard>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { formatSectionDateLabel, isStartDateInFuture } from "~vue/composables/useCtaDateRange";
import type { DecoratedPerformanceReview } from "~vue/types/performance_management";
import { logUserInteraction } from "~vue/utils/logUtils";
import { sortBy, uniq } from "lodash";
import { computed, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { getReviewGroupStatus, getReviewGroupStatusState } from "./composables/useReviewGroupStatus";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "./constants";
import PerformanceManagementCTA from "./PerformanceManagementCTA.vue";

const props = withDefaults(
    defineProps<{
        dueDate?: string | null;
        startDate?: string | null;
        otherTitle?: string;
        otherSubtitle?: string;
        reviews: DecoratedPerformanceReview[];
        momentId?: string | number | null;
        currentReviewId?: string | null;
        isMomentReadOnly?: boolean;
        /** An answer to a detected-team-member prompt is in flight; every prompt waits for it. */
        isResolvingTeamMember?: boolean;
    }>(),
    { dueDate: null, startDate: null, otherTitle: "", otherSubtitle: "", momentId: null, currentReviewId: null, isResolvingTeamMember: false },
);

defineEmits(["edit-list", "cta-convo-clicked", "completion-toggled", "confirm-relationship", "decline-relationship"]);

const { t } = useI18n();

const dateLabel = computed(() => formatSectionDateLabel(props.startDate, props.dueDate));

// Section hasn't opened yet — disable/grey its CTAs until the start date arrives.
const isSectionLocked = computed(() => isStartDateInFuture(props.startDate));

onMounted(() => {
    if (props.reviews?.length) {
        logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}team_section_shown`, { moment_id: props.momentId });
    }
});

const reviewsByRelationship = computed(() => {
    const reviewsWithRelationships = props.reviews.filter((review) => !!review.relationship);
    const result: Record<string, DecoratedPerformanceReview[]> = {};
    const relationshipIds = uniq(sortBy(reviewsWithRelationships, (review) => review.relationship?.reviewee_name).map((review) => review.relationship?.id));
    for (const relationshipId of relationshipIds) {
        const relationshipReviews = reviewsWithRelationships.filter((review) => review.relationship?.id === relationshipId);
        result[String(relationshipId)] = sortBy(relationshipReviews, (review) => review.cta?.id); // TODO: Sort this by something admin-specified rather than CTA ID
    }
    return result;
});

const shouldShowEditTeamButton = computed(() => !props.isMomentReadOnly && Object.keys(reviewsByRelationship.value).length);

// Track expanded/collapsed state for each colleague card
const expandedCards = ref<Record<string | number, boolean>>({});

const toggleCard = (relationshipId: number | string) => {
    expandedCards.value[relationshipId] = !expandedCards.value[relationshipId];
    logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}team_member_card_toggled`, {
        relationship_id: relationshipId,
        new_state: expandedCards.value[relationshipId] ? "open" : "closed",
    });
};

// Initialize first colleague card as expanded when relationships load
watch(reviewsByRelationship, (data) => {
    if (data) {
        const firstRelationshipId = Object.keys(data)[0];
        if (firstRelationshipId && expandedCards.value[firstRelationshipId] === undefined) {
            expandedCards.value[firstRelationshipId] = true;
        }
    }
});
</script>
